import { SubmitButton } from 'components/common/buttons/SubmitButton';
import ErrorMessage from 'components/common/ErrorMessage';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { fetchAppointmentsInterpreters, inviteInterpretersSelector } from 'slices/inviteInterpreterSlice';
import { Select } from './Select';
import { useTranslation } from 'react-i18next';

function SelectInterpreterModal({
    isOpen,
    onClose,
    onConfirm,
    appointmentId,
    items,
    previewEmailOnSubmit = false,
    endpoint = "assign",
}) {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [loadingInterpreters, setLoadingInterpreters] = useState(false);
    const { allInterpreters } = useSelector(inviteInterpretersSelector);

    const interpreterOptions = React.useMemo(() => {
        if (!allInterpreters) return [];
        return allInterpreters.map(interpreter => ({
            value: interpreter.id,
            label: interpreter.first_name + ' ' + interpreter.last_name,
        }));
    }, [allInterpreters]);

    const methods = useForm();
    const { handleSubmit, formState: { errors }, control, setValue } = methods;

    const [interpreterState, setInterpreterState] = useState(null);

    useEffect(() => {
        methods.reset({ interpreter_id: null });
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && appointmentId) {
            setLoadingInterpreters(true);
            dispatch(fetchAppointmentsInterpreters(appointmentId))
                .finally(() => setLoadingInterpreters(false));
        }
    }, [isOpen, appointmentId]);

    useEffect(() => {
        if (isOpen && items?.interpreter_id && items?.interpreter) {
            const selected = {
                value: items.interpreter_id,
                label: items.interpreter.first_name + ' ' + items.interpreter.last_name,
            };
            setInterpreterState(selected);
            setValue('interpreter_id', items.interpreter_id);
        } else {
            setInterpreterState(null);
            setValue('interpreter_id', null);
        }
    }, [isOpen, items, setValue]);

    const actionLabel = endpoint === "reassign" ? t("appointmentView.reassign") : t("appointmentView.assign");

    return (
        <Modal isOpen={isOpen} toggle={onClose} centered className="modal-medium">
            <form onSubmit={handleSubmit((data) => {
                setLoading(true);
                const payload = {
                    ...data,
                    ...(previewEmailOnSubmit ? { preview_reassign_email: true } : {}),
                };
                onConfirm(payload, endpoint, () => { setLoading(false); });
            })}>
                <ModalHeader className='header_modal align-items-center'>
                    <h5 className="modal-title_u">{actionLabel} {t("selectInterpreter.vendor")}</h5>
                </ModalHeader>
                <ModalBody className='modal_form_body'>
                    <div className='step_cards step_1'>
                        <h2>{actionLabel} {t("selectInterpreter.vendor")}</h2>
                        <div className="row-primary">
                            <h2>{t("selectInterpreter.selectVendor")}</h2>
                            <Controller
                                name="interpreter_id"
                                control={control}
                                rules={{ required: t("selectInterpreter.selectRequired") }}
                                render={({ field }) => (
                                    <div style={{ width: '100%' }}>
                                        <Select
                                            name="interpreter_id"
                                            options={interpreterOptions}
                                            loading={loadingInterpreters}
                                            value={field.value
                                                ? interpreterOptions.find(opt => opt.value == field.value) ?? null
                                                : interpreterState
                                            }
                                            onChange={(rawValue) => {
                                                // Select component calls onChange with just the raw value (option.value)
                                                const selected = interpreterOptions.find(opt => opt.value == rawValue) ?? null;
                                                setInterpreterState(selected);
                                                field.onChange(rawValue ?? null);
                                            }}
                                        />
                                        {errors?.interpreter_id?.message && (
                                            <div style={{ marginBottom: 10 }}>
                                                <ErrorMessage text={errors.interpreter_id.message} />
                                            </div>
                                        )}
                                    </div>
                                )}
                            />
                        </div>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <SubmitButton btnText={t("translation.submit")} disabled={loadingInterpreters} loader={loading} />
                    <button className="table_delete" type='button' onClick={onClose}>{t("billing.cancel")}</button>
                </ModalFooter>
            </form>
        </Modal>
    );
}

export default SelectInterpreterModal;
